import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import {
  CompanyInfo,
  DomainAvailability,
  GSTInfo,
  OTPToken,
  Tenant,
  TenantCreate,
  TenantLookupRequest,
  TenantLookupResponse,
  TenantSignUp,
  TenantStore,
} from '@services/tenant-services/models';
import { TenantApiService } from '@services/tenant-services/tenant-api.service';
import { SignupUser } from '@services/onboarding-services/models';
import { Store } from '@services/setting-services/models';
import { User } from '@services/core-services/models';
import type { CouponId } from '@services/coupon-services/models/coupon-id';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly tenant = new BehaviorSubject<Tenant | undefined>(undefined);

  constructor(private readonly api: TenantApiService) {}

  /**
   * Sets tenant into local state.
   * @param tenant
   */
  setTenant(tenant: Tenant | undefined) {
    this.tenant.next(tenant);
    return tenant;
  }

  /**
   * Gets tenant observable.
   */
  getTenant() {
    return this.tenant.asObservable();
  }

  /**
   * Gets tenant from local state.
   */
  getCurrentTenant() {
    return this.tenant.getValue();
  }

  /**
   * Gets first completed store for the tenant
   */
  getCompletedStore(user?: User) {
    const tenant = this.getCurrentTenant();
    // Try to find user default store and check if it is completed
    const defaultStore = tenant?.stores.find(
      s => s.id === user?.default_store_id && s.is_store_setup_complete
    );

    if (defaultStore) {
      return defaultStore;
    }

    // If not user default store is defined than going to find any completed store for tenant.
    return tenant?.stores.find(s => s.is_store_setup_complete);
  }

  /**
   * Creates a new tenant
   * @param payload
   */
  createTenant(payload: TenantCreate): Observable<Tenant> {
    return this.api
      .post<any, any>('tenants', payload)
      .pipe(map(response => response.content.tenant as Tenant));
  }

  /**
   * Method gets tenant by its domain.
   * @param domain
   */
  getTenantByDomain(domain: string): Observable<Tenant> {
    return this.lookupTenant({ domain });
  }

  /**
   * Method gets tenant by its subdomain.
   * @param subdomain
   */
  getTenantBySubdomain(subdomain: string): Observable<Tenant> {
    return this.lookupTenant({ subdomain });
  }

  /**
   * Lookups for the tenant base of the lookup query.
   * @param request
   */
  lookupTenant(request: TenantLookupRequest): Observable<Tenant> {
    return this.api.get<TenantLookupResponse>('tenants/lookup', request).pipe(
      map(response => {
        return response.content.tenant;
      })
    );
  }

  /**
   * Verify GST Number
   * @param num
   */
  verifyGSTNo(num: string | number): Observable<GSTInfo> {
    return this.api
      .get<any>(`tenants/verify-gstno/${num}`)
      .pipe(map(response => response.content.data as GSTInfo));
  }

  /**
   * Verify CIN (Company Identification Number)
   * @param num
   */
  verifyCIN(num: string | number): Observable<CompanyInfo> {
    return this.api
      .get<any>(`tenants/verify-cin/${num}`)
      .pipe(map(response => response.content.data as CompanyInfo));
  }

  /**
   * Check user identifier
   * @param uid
   */
  checkUserIdentifier(uid: string): Observable<CompanyInfo> {
    return this.api
      .get<any>(`tenants/check-uid/${uid}`)
      .pipe(map(response => response.content.data as CompanyInfo));
  }

  /**
   * Check tenant subdomain availability
   * @param subdomain
   */
  checkSubdomainAvailability(subdomain: string): Observable<DomainAvailability> {
    return this.api
      .get<any>(`tenants/subdomain-availability`, { subdomain })
      .pipe(map(response => response.content as DomainAvailability));
  }

  /**
   * Gets a list of the tenant's stores.
   * @param id
   */
  getStoresForTenant(id: string): Observable<TenantStore[]> {
    return this.api.get(`tenants/${id}/stores`).pipe(
      map((response: any) => {
        return response.content.stores;
      })
    );
  }

  /**
   * Registers new tenant
   * @param payload
   */
  signUpWithEmailAndPassword(payload: TenantSignUp) {
    return this.api.post<TenantSignUp, OTPToken>(`tenants/signup-with-email`, payload).pipe(
      map((response: any) => {
        return response.content as OTPToken;
      })
    );
  }

  /**
   * Sends OTP code to the specified email to start email verification process for signup.
   * @param email
   */
  sendVerificationOTP(email: string) {
    return this.api.post<any, any>(`tenants/send-otp`, { email, otp_action: 'sign-up' }).pipe(
      map((response: any) => {
        return response.content;
      })
    );
  }

  /**
   * Verifies email using otp token.
   * @param otp
   * @param token
   */
  verifyOTP(otp: string, token: string) {
    return this.api
      .post<any, any>(`tenants/verify-otp`, { otp, otp_token: token, otp_action: 'sign-up' })
      .pipe(
        map((response: any) => {
          return response.content.user as SignupUser;
        })
      );
  }

  /**
   * Sends subdomain recovery email
   * @param email
   * @param continueUrl
   */
  sendSubdomainRecoveryEmail(email: string, continueUrl: string) {
    return this.api.post<any, any>(`tenants/send-custom-email`, {
      email,
      action: 'recover-subdomain',
      continue_url: continueUrl,
    });
  }

  /**
   * Updates tenant store info
   * @param store
   */
  updateStoreInfo(store: TenantStore) {
    const tenant = this.tenant.getValue();
    if (tenant) {
      tenant.stores = tenant!.stores.map(s =>
        s.id === store.id ? { ...s, ...store } : s
      ) as Store[];
      this.tenant.next(tenant);
    }
  }

  /**
   * Adds tenant store info
   * @param store
   */
  addStore(store: TenantStore) {
    const tenant = this.tenant.getValue();
    if (tenant) {
      tenant.stores = tenant!.stores.concat([store as Store]);
      this.tenant.next(tenant);
    }
  }

  /**
   * Uploads tenant image and returns image_url
   * @param file
   */
  uploadTenantImage(file: File): Observable<{ image_url: string }> {
    const tenant = this.tenant.getValue();
    const data = new FormData();
    data.set('file', file);
    return this.api
      .post<any, any>(`tenants/${tenant!.id}/upload-image`, data)
      .pipe(map(response => response.content));
  }

  /**
   * Updates tenant info
   * @param id
   * @param payload
   */
  updateTenant(id: string, payload: Partial<Tenant>): Observable<Tenant> {
    return this.api.put<Partial<Tenant>, any>(`tenants/${id}`, payload).pipe(
      map(response => response.content.tenant as Tenant),
      tap(tenant => this.tenant.next(tenant))
    );
  }

  /**
   * Updates tenant info
   * @param id
   */
  deleteTenant(id: string) {
    return this.api.delete(`tenants/${id}`);
  }
}
